import React, {
  useCallback,
  useRef,
  useState,
  useEffect,
  useMemo,
} from 'react';
import { Button } from 'react-bootstrap';
import { toast } from 'react-toastify';

import { useUserContext } from 'providers/UserProvider';

import Table from 'components/common/Table';

import { shipmentApi } from 'api';

import {
  getShipmentListTab,
  getShipmentListColumns,
  SHIPMENT_FILTER_OPTIONS,
} from 'constants/shipment';
import { FILTER_TYPES } from 'constants/filterType';

const Shipment = () => {
  const tableRef = useRef();
  const { selectedTenant, selectedWarehouse } = useUserContext();
  const tenant = selectedTenant?.domains?.[0]?.domain;
  const warehouse = selectedWarehouse?.domain;

  const initialFilter = { warehouse: selectedWarehouse?.id };

  const [shippingProviders, setShippingProviders] = useState([]);
  const [loadingShipmentId, setLoadingShipmentId] = useState(null);

  useEffect(() => {
    const fetchProviders = async () => {
      try {
        const response = await shipmentApi.getShippingProviders();
        const results = response?.results || response || [];
        setShippingProviders(results);
      } catch {
        setShippingProviders([]);
      }
    };
    fetchProviders();
  }, []);

  const filterOptions = useMemo(() => {
    const providerOptions = shippingProviders.map(p => ({
      value: p.id,
      label: p.shipping_provider_type,
    }));

    return [
      ...SHIPMENT_FILTER_OPTIONS.slice(0, -1),
      {
        field: 'shipping_provider',
        label: 'Shipping Provider',
        type: FILTER_TYPES.MULTI_SELECT,
        options: providerOptions,
      },
      SHIPMENT_FILTER_OPTIONS[SHIPMENT_FILTER_OPTIONS.length - 1],
    ];
  }, [shippingProviders]);

  const exportApi = useCallback(params => {
    return shipmentApi.exportShipment(params);
  }, []);

  const handleDirectAwb = async shipmentId => {
    setLoadingShipmentId(shipmentId);
    try {
      const response = await shipmentApi.directAwb(shipmentId);

      if (response?.shipment_status === 'ready_to_ship') {
        toast.success('AWB generated successfully');
      } else {
        toast.warning(
          'AWB generation failed. Please try Manual AWB or Refresh Shipping.',
        );
      }
    } catch (error) {
      const message =
        error?.response?.data?.non_field_errors?.[0] ||
        'Failed to process Direct AWB';
      toast.error(message);
    } finally {
      setLoadingShipmentId(null);
      tableRef.current?.refetch();
    }
  };

  const columns = useMemo(() => {
    const baseColumns = getShipmentListColumns(tenant, warehouse);

    if (!selectedWarehouse?.is_picklist_pass_enabled) {
      return baseColumns;
    }

    const directAwbColumn = {
      accessorKey: 'direct_awb',
      header: '',
      enableSorting: false,
      cell: ({ row: { original } }) => {
        const { id, shipment_status, picklist } = original;

        if (shipment_status === 'created' && picklist == null) {
          return (
            <Button
              variant="outline-success"
              size="sm"
              disabled={loadingShipmentId === id}
              onClick={() => handleDirectAwb(id)}
            >
              {loadingShipmentId === id ? 'Processing...' : 'Direct AWB'}
            </Button>
          );
        }

        return null;
      },
    };

    return [...baseColumns, directAwbColumn];
  }, [
    tenant,
    warehouse,
    selectedWarehouse?.is_picklist_pass_enabled,
    loadingShipmentId,
  ]);

  return (
    <Table
      ref={tableRef}
      title="Shipments"
      apiService={shipmentApi}
      exportApi={exportApi}
      sortable={true}
      columns={columns}
      filterOptions={filterOptions}
      tabOptions={getShipmentListTab(initialFilter)}
      initialFilter={initialFilter}
    />
  );
};

export default Shipment;
